/**
 * `/pending` — landing for any account whose `is_active=false`.
 *
 * Renders one of three variants depending on `status`:
 *
 *   pending    awaiting admin review — auto-refresh every 30s, friendly
 *   suspended  admin temporarily blocked — calmer tone, "contact admin"
 *   rejected   admin denied — terminal state, no auto-refresh
 *
 * Active users hitting this URL bounce back to /app via the page effect
 * (their /me returns active on first call).
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Clock, Ban, ShieldOff } from 'lucide-react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/Button'
import { useAuthStore, type UserStatus } from '@/stores/auth'
import { useMe, useLogout } from '@/hooks/useAuthApi'

export default function Pending() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useLogout()

  // Status fallback: rows from before migration 78 lacked `status`;
  // treat as 'pending' so they see the friendly screen rather than blank.
  const status: UserStatus = (user?.status as UserStatus) ?? 'pending'

  // Auto-refresh /me — but only for `pending` (transient state). For
  // suspended/rejected, polling adds nothing — admin action will land
  // and the next manual reload picks it up.
  const me = useMe({ enabled: status === 'pending' })
  useEffect(() => {
    if (me.data?.is_active) navigate('/app', { replace: true })
  }, [me.data?.is_active, navigate])

  if (status === 'suspended') {
    return (
      <AuthLayout
        title={t('auth:suspended.title')}
        subtitle={t('auth:suspended.subtitle')}
      >
        <div className="text-center py-6">
          <IconCircle tone="rose"><Ban size={20} className="text-rose" /></IconCircle>
          <p className="mt-4 text-sm text-text">
            <Greeting email={user?.email ?? '—'} />
          </p>
          <p className="mt-2 text-sm text-textDim">
            {t('auth:suspended.body')}
          </p>
          <p className="mt-4 text-xs text-textFaint">
            {t('auth:suspended.contact')}
          </p>
          <div className="mt-6">
            <Button variant="ghost" size="sm" onClick={logout}>
              {t('common:actions.log_out')}
            </Button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  if (status === 'rejected') {
    return (
      <AuthLayout
        title={t('auth:rejected.title')}
        subtitle={t('auth:rejected.subtitle')}
      >
        <div className="text-center py-6">
          <IconCircle tone="rose"><ShieldOff size={20} className="text-rose" /></IconCircle>
          <p className="mt-4 text-sm text-textDim">
            {t('auth:rejected.body')}
          </p>
          <div className="mt-6">
            <Button variant="ghost" size="sm" onClick={logout}>
              {t('common:actions.log_out')}
            </Button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // status === 'pending' (default)
  return (
    <AuthLayout
      title={t('auth:pending.title')}
      subtitle={t('auth:pending.subtitle')}
    >
      <div className="text-center py-6">
        <IconCircle tone="amber"><Clock size={20} className="text-amber" /></IconCircle>
        <p className="mt-4 text-sm text-text">
          <Greeting email={user?.email ?? '—'} />
        </p>
        <p className="mt-2 text-sm text-textDim">
          {t('auth:pending.body')}
        </p>

        <p className="mt-6 text-xs text-textFaint font-mono">
          {t('auth:pending.auto_refresh')}
        </p>

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => me.refetch()}
            loading={me.isFetching}
          >
            {t('common:actions.check_now')}
          </Button>
          <Button variant="ghost" size="sm" onClick={logout}>
            {t('common:actions.log_out')}
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}

/* ─── helpers ─────────────────────────────────────────────────────── */

function IconCircle({
  tone, children,
}: { tone: 'amber' | 'rose'; children: React.ReactNode }) {
  const bg = tone === 'amber' ? 'bg-amber-faint' : 'bg-rose-faint'
  return (
    <div className={`w-12 h-12 mx-auto grid place-items-center ${bg} rounded-full`}>
      {children}
    </div>
  )
}

function Greeting({ email }: { email: string }) {
  return (
    <Trans
      i18nKey="auth:pending.greeting"
      values={{ email }}
      components={{ 1: <span className="font-mono" /> }}
    />
  )
}
